import React, { useState } from "react";
import {
  Button,
  Stack,
  Typography,
  TextField,
  IconButton,
  useTheme,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import PersianBirthdayPicker from "./Calender";
import { useRegisterMutation } from "../../../Store/Slices/rtk";
import { setUserData } from "../../../Store/Slices/reducer";
import { updateCartOnServer } from "../../../Store/Slices/CartSlice/cartSlice";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { GradientBorderBox } from "../../../Components";

export default function Register() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tempToken, phoneNumber } = useSelector((state) => state.authSlice);

  const [timestamps, setTimestamps] = useState();
  const [registerUser] = useRegisterMutation();
  const cartItems = useSelector((state) => state.cartSlice.list);

  const formik = useFormik({
    initialValues: {
      phone_number: phoneNumber || "",
      tmp_token: tempToken || "",
      first_name: "",
      last_name: "",
      national_code: "",
      mail: "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("نام الزامی است"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const res = await registerUser({
          phoneNumber: values.phone_number,
          tempToken: values.tmp_token,
          firstName: values.first_name,
          lastName: values.last_name,
          nationalCode: values.national_code || null,
          birth: timestamps || null,
          mail: values.mail || null,
        }).unwrap();

        dispatch(
          setUserData({
            token: res.data.token,
            user: res.data.user,
          }),
        );

        for (const item of cartItems) {
          await dispatch(
            updateCartOnServer({
              product_code: item.code,
              variant_code: item.variant_code,
              qty: item.quantity,
            }),
          );
        }

        navigate("/");
        toast.success("ثبت نام با موفقیت انجام شد");
      } catch (error) {
        console.error("Registration error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Stack sx={{ position: "relative", width: "100%", height: "100vh" }}>
      <Stack
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
          // height: { xs: "400px", md: "500px", xl: "670px" , xxl:"700px"},
          backgroundImage: 'url("/media/Layout/AuthLayout/OTP/group.svg")',
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "30px",
          width: "100%",
          maxWidth: { xs: "270px", sm: "350px", md: "600px", xl: "800px" },
        }}
      >
        {/* Back Button */}
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            position: "absolute",
            top: { xs: "10px", sm: "15px" },
            left: { xs: "10px", sm: "15px" },
            color: theme.palette.primary.main,
            zIndex: 2001,
          }}
        >
          <KeyboardBackspaceIcon />
        </IconButton>

        {/* Form */}
        <Stack
          onSubmit={formik.handleSubmit}
          component={"form"}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            gap: "20px",
            width: { xs: "80%", sm: "50%", md: "50%" },
            mt: { xs: "10px", md: "20px" },
            mx: "auto",
          }}
        >
          <Typography
            sx={{
              fontSize: { lg: "24px", sm: "18px", xs: "15px" },
              textAlign: "center",
              fontWeight: "bold",
              mb: "1.5rem",
            }}
          >
            ثبت نام
          </Typography>

          {/* First Name */}
          <Stack sx={{ width: "100%" }}>
            <Typography sx={{ mb: "6px", fontSize: "14px" }}>نام</Typography>
            <TextField
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                  bgcolor: theme.palette.background.paper,
                  border: "1px solid gray",
                },
                "& .MuiOutlinedInput-input": { padding: "8px" },
              }}
              name="first_name"
              value={formik.values.first_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Stack>

          {/* Last Name */}
          <Stack sx={{ width: "100%" }}>
            <Typography sx={{ mb: "6px", fontSize: "14px" }}>
              نام خانوادگی
            </Typography>
            <TextField
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                  bgcolor: theme.palette.background.paper,
                  border: "1px solid gray",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px",
                },
              }}
              name="last_name"
              value={formik.values.last_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.last_name && Boolean(formik.errors.last_name)
              }
              helperText={formik.touched.last_name && formik.errors.last_name}
            />
          </Stack>
          {/* Birthday */}
          <Stack sx={{ width: "100%" }}>
            <Typography sx={{ mb: "6px", fontSize: "14px" }}>
              تاریخ تولد
            </Typography>
            <PersianBirthdayPicker setTimestamps={setTimestamps} />
          </Stack>

          {/* National Code */}
          <Stack sx={{ width: "100%" }}>
            <Typography sx={{ mb: "6px", fontSize: "14px" }}>کد ملی</Typography>
            <TextField
              fullWidth
              type="number"
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                  bgcolor: theme.palette.background.paper,
                  border: "1px solid gray",
                },
                "& .MuiOutlinedInput-input": { padding: "8px" },
              }}
              name="national_code"
              value={formik.values.national_code}
              onChange={formik.handleChange}
            />
          </Stack>

          {/* Email */}
          <Stack sx={{ width: "100%" }}>
            <Typography sx={{ mb: "6px", fontSize: "14px" }}>ایمیل</Typography>
            <TextField
              fullWidth
              type="email"
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                  bgcolor: theme.palette.background.paper,
                  border: "1px solid gray",
                },
                "& .MuiOutlinedInput-input": { padding: "8px" },
              }}
              name="mail"
              value={formik.values.mail}
              onChange={formik.handleChange}
            />
          </Stack>

          {/* Submit */}
          <Button
            type="submit"
            variant="contained"
            sx={{
              width: "100%",
              mt: "1rem",
              py: ".6rem",
              color: theme.palette.secondary.main,
            }}
          >
            ثبت
          </Button>
        </Stack>

        <GradientBorderBox />
      </Stack>
    </Stack>
  );
}
